import { Button, Stack } from '@mui/material';
import {
  DEFAULT_DATE_FORMAT_FNS,
  FilterKeys,
  GetPayableFeesArrangeBy,
  GetPayableFeesQueryFilters,
  GetPayableFeesQuerySort,
  PayableFee,
  useGetPayableFeesQuery,
  useInvoiceActionMutation,
} from '@schooly/api';
import { useInvalidateListQueriesFor } from '@schooly/components/filters';
import { useNotifications } from '@schooly/components/notifications';
import { EyeIcon, GridBody, MainPageGrid, SkeletonRows } from '@schooly/style';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { format, lastDayOfMonth } from 'date-fns';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { PayableFeeRow, PayableFeesHeader } from './PayableFeesGrid';

type ArrangedByPayableFeesGridProps = {
  count: number;
  filters: GetPayableFeesQueryFilters;
  arrangeByKey?: GetPayableFeesArrangeBy;
  arrangeByValue?: string | number;
  schoolId: string;
  sort?: GetPayableFeesQuerySort;
  query?: string;
  onChangeSort: (v: GetPayableFeesQuerySort) => void;
  onUpdateTotalCount?: (v: number) => void;
};

const PAGE_SIZE = 30;
const HEADER_OFFSET = 75;

export const ArrangedByPayableFeesGrid: FC<ArrangedByPayableFeesGridProps> = ({
  count,
  sort,
  filters: initialFilters,
  schoolId,
  arrangeByKey,
  arrangeByValue,
  onChangeSort,
  onUpdateTotalCount,
}) => {
  const filters = useMemo(() => {
    if (!arrangeByKey || !arrangeByValue) return initialFilters;

    switch (arrangeByKey) {
      case FilterKeys.Month:
        const startDate = arrangeByValue + '';
        if (!startDate) return initialFilters;

        return {
          ...initialFilters,
          [FilterKeys.Date]: [
            startDate,
            format(lastDayOfMonth(newDateTimezoneOffset(startDate)), DEFAULT_DATE_FORMAT_FNS),
          ],
        };

      // case FilterKeys.ProductType:
      case FilterKeys.Payer:
      case FilterKeys.Product:
      case FilterKeys.DayPastDue:
      case FilterKeys.InvoicesAgeGroup:
        return {
          ...initialFilters,
          [arrangeByKey]: [arrangeByValue === undefined ? 'none' : arrangeByValue],
        };
      default:
        const _never: never = arrangeByKey;
        return _never;
    }
  }, [initialFilters, arrangeByKey, arrangeByValue]);

  const { data, isLoading, params, setParams, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useGetPayableFeesQuery(
      {
        school_ids: schoolId,
        page_size: PAGE_SIZE,
        sort,
        filters,
        query: '',
      },
      { refetchOnMount: 'always' },
    );

  const { showError, showNotification } = useNotifications();
  const invoiceAction = useInvoiceActionMutation();
  const invalidateQueries = useInvalidateListQueriesFor('payableFee');

  const handleVoidInvoice = useCallback(
    (invoiceId: string) => {
      return invoiceAction.mutateAsync(
        { invoice_ids: [invoiceId], action: 'void' },
        {
          onSuccess: () => {
            invalidateQueries();
            showNotification({
              textId: 'payableFees-InvoiceUpdated',
              type: 'success',
            });
          },
          onError: showError,
        },
      );
    },
    [invalidateQueries, invoiceAction, showError, showNotification],
  );

  const total = data?.pages[0]?.count;

  useEffect(() => {
    if (!onUpdateTotalCount) return;
    if (total === undefined) return;

    onUpdateTotalCount(total);
  }, [total, onUpdateTotalCount]);

  useEffect(() => {
    setParams((params) => ({ ...params, filters, sort }));
  }, [sort, filters, setParams]);

  const entries = useMemo(
    () => data?.pages.reduce<PayableFee[]>((prev, curr) => [...prev, ...curr.results], []) ?? [],
    [data?.pages],
  );

  return (
    <MainPageGrid
      bottomElement={
        hasNextPage && !isFetchingNextPage ? (
          <Stack mt={1} alignItems="center">
            <Button startIcon={<EyeIcon />} variant="text" onClick={() => fetchNextPage()}>
              <FormattedMessage id="action-ShowMoreButton" />
            </Button>
          </Stack>
        ) : undefined
      }
    >
      <PayableFeesHeader
        sort={params.sort}
        onChangeSort={onChangeSort}
        sx={{
          position: 'sticky',
          bgcolor: 'white',
          zIndex: 3,
          top: HEADER_OFFSET,
        }}
      />
      <GridBody>
        {entries?.map((entry) => {
          const [student] = entry.students;
          const key = `${entry.status}-${entry.due_date}-${entry.issue_date}-${
            entry.invoice_date
          }-${student ? student.id : ''}`;

          return <PayableFeeRow key={key} payableFee={entry} onVoidInvoice={handleVoidInvoice} />;
        })}
        {(isLoading || isFetchingNextPage) && (
          <SkeletonRows
            columnsCount={11}
            amount={Math.min(PAGE_SIZE, count - (entries?.length || 0))}
          />
        )}
      </GridBody>
    </MainPageGrid>
  );
};
